import React from 'react';
import {
  Text,
  Card,
  Stack,
  useTheme,
  Icons,
  Button,
  styled,
} from '@/theme/index';
import { UnclaimedMoneyRowProps } from '../../interfaces';

const StyledCard = styled(Card)<{ isEven: boolean }>(({ theme, isEven }) => ({
  backgroundColor: isEven
    ? theme.palette?.unclaimedMoneyGrid?.light
    : theme.palette?.unclaimedMoneyGrid?.contrastText,
  borderBottom: `8px solid ${theme.palette.unclaimedMoneyGrid?.main}`,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  width: theme.spacing(14.75),
  maxHeight: theme.spacing(3.75),
  marginTop: theme.spacing(1.5),
  whiteSpace: 'nowrap',
  fontSize: '0.875rem',
  backgroundColor: theme.palette?.unclaimedMoneyGrid?.main,
}));

interface UnclaimedMoneyInfoRowProps {
  /** The icon to display on the left side of the row. */
  icon: React.ReactNode;
  /** The label to display on the left side of the row. */
  label: string;
}

/**
 * Represents a row in the unclaimed money results grid.
 */
function UnclaimedMoneyInfoRow({ icon, label }: UnclaimedMoneyInfoRowProps) {
  return (
    <Stack direction="row" spacing={2} alignItems="start" marginBottom="8px">
      {icon}
      <Text fontSize="13px">{label}</Text>
    </Stack>
  );
}

export interface MobileUnclaimedMoneyResultsProps {
  /** The list of unclaimed money results */
  unclaimedMoneyRow: UnclaimedMoneyRowProps;
  /** Index to determine row styling */
  rowIndex: number;
}

/**
 * This component is responsible for displaying the unclaimed money results grid in mobile view.
 */
export function MobileUnclaimedMoneyResults({
  unclaimedMoneyRow,
  rowIndex,
}: MobileUnclaimedMoneyResultsProps) {
  const theme = useTheme();

  return (
    <StyledCard isEven={rowIndex % 2 === 0}>
      <Text variant="h6">{unclaimedMoneyRow.name}</Text>
      <Stack spacing={1} marginTop={theme.spacing(1)}>
        <UnclaimedMoneyInfoRow
          icon={<Icons.NearMe fontSize="small" />}
          label={unclaimedMoneyRow.address}
        />
        <UnclaimedMoneyInfoRow
          icon={<Icons.Business fontSize="small" />}
          label={unclaimedMoneyRow.reportedBy}
        />
        <UnclaimedMoneyInfoRow
          icon={<Icons.Place fontSize="small" />}
          label={unclaimedMoneyRow.heldIn}
        />
        <UnclaimedMoneyInfoRow
          icon={<Icons.AttachMoney fontSize="small" />}
          label={unclaimedMoneyRow.amountHeld}
        />
        <Text fontSize="12px">{unclaimedMoneyRow.reportedDate}</Text>
      </Stack>
      <ActionButton variant="contained">File claim</ActionButton>
    </StyledCard>
  );
}
